import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Building2, BadgePercent, MapPin, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { CONTACT } from "../data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VEHICLES = ["Furgoni", "Camion", "Auto aziendali", "Minibus / Van"];
const PERKS = [
  { icon: Building2, title: "Direttamente in azienda", text: "Interveniamo nel tuo piazzale o deposito, senza fermare l'operatività." },
  { icon: BadgePercent, title: "Prezzi agevolati flotte", text: "Sconti dedicati per flotte con più di 5 mezzi e contratti periodici." },
  { icon: MapPin, title: "Preventivo gratuito", text: "Sopralluogo e preventivo senza impegno, risposta entro 24-48 ore." },
];

const inputCls =
  "w-full rounded-xl bg-[#050B18]/70 border border-slate-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#7EC8E3] focus:ring-1 focus:ring-[#7EC8E3] transition-colors";

const EMPTY = { company: "", contact_name: "", phone: "", email: "", vehicles_count: "", location: "", notes: "" };

export const Business = () => {
  const [form, setForm] = useState(EMPTY);
  const [types, setTypes] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) => setTypes((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const isFleet = Number(form.vehicles_count) > 5;

  const submit = async (e) => {
    e.preventDefault();
    if (types.length === 0) return toast.error("Seleziona almeno un tipo di mezzo");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/fleet-quotes`, {
        ...form, vehicles_count: Number(form.vehicles_count), vehicle_types: types,
      });
      setDone({
        ...data,
        name: form.contact_name,
        whatsappUrl: `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
          [
            "Ciao Cosimiroclean! Ho appena inviato una richiesta di preventivo per la mia azienda.",
            `Azienda: ${form.company}`,
            `Referente: ${form.contact_name}`,
            `Telefono: ${form.phone}`,
            `Tipo di mezzi: ${types.join(", ")}`,
            `Numero mezzi: ${form.vehicles_count}`,
            `Luogo / sede: ${form.location}`,
            form.notes ? `Note: ${form.notes}` : null,
            "Potete inviarmi un preventivo?",
          ]
            .filter(Boolean)
            .join("\n")
        )}`,
      });
      toast.success("Richiesta inviata! Ti ricontatteremo a breve.");
      setForm(EMPTY); setTypes([]);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o contattaci su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="aziende" data-testid="business-section" className="relative py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div>
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#7EC8E3] font-semibold">Aziende &amp; flotte</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
              Pulizia di <span className="text-metallic">furgoni e camion</span> per la tua azienda
            </h2>
            <p className="text-slate-300 mt-5 leading-relaxed">
              Mezzi puliti e curati sono il biglietto da visita della tua impresa. Veniamo direttamente
              nella tua sede per la pulizia interna dei veicoli commerciali, con{" "}
              <span className="text-[#A0E0FF] font-semibold">prezzi agevolati per flotte oltre 5 mezzi</span>.
            </p>
            <div className="mt-8 space-y-4">
              {PERKS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div key={p.title} data-testid={`business-perk-${i + 1}`}
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.1 }}
                    className="flex gap-4 items-start card-metallic rounded-2xl p-4">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#0D1B4A] to-[#0A1A3A] border border-[#7EC8E3]/40 flex items-center justify-center">
                      <Icon size={22} className="text-[#A0E0FF]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-100">{p.title}</h4>
                      <p className="text-sm text-slate-400 mt-1">{p.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }} className="card-metallic rounded-3xl p-6 sm:p-8">
            {done ? (
              <div data-testid="business-confirmation" className="text-center py-8">
                <CheckCircle2 size={56} className="mx-auto text-[#7EC8E3]" />
                <h3 className="text-2xl font-bold mt-4">Grazie, {done.name}!</h3>
                <p className="text-slate-300 mt-3">
                  Abbiamo ricevuto la tua richiesta di preventivo. Ti ricontatteremo entro 24-48 ore.
                </p>
                {done.customer_email_sent && (
                  <p className="text-sm text-[#A0E0FF] mt-2">Ti abbiamo inviato un'email con il riepilogo.</p>
                )}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                  <a href={done.whatsappUrl} target="_blank" rel="noreferrer" data-testid="business-whatsapp-recap"
                    className="btn-silver rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                    <MessageCircle size={18} /> Invia riepilogo su WhatsApp
                  </a>
                  <button data-testid="business-new-request" onClick={() => setDone(null)} className="btn-outline-blue rounded-full px-6 py-3 font-semibold w-full sm:w-auto">
                    Nuova richiesta
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="business-form">
                <div className="flex items-center gap-2 mb-5">
                  <Truck size={20} className="text-[#7EC8E3]" />
                  <h3 className="text-xl font-bold">Richiedi un preventivo gratuito</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="biz-company" className={inputCls} placeholder="Azienda *" required value={form.company} onChange={(e) => set("company", e.target.value)} />
                  <input data-testid="biz-contact" className={inputCls} placeholder="Referente *" required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
                  <input data-testid="biz-phone" className={inputCls} placeholder="Telefono *" required value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  <input data-testid="biz-email" type="email" className={inputCls} placeholder="Email *" required value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="mt-4">
                  <div className="text-sm text-slate-300 mb-2">Tipo di mezzi *</div>
                  <div className="flex flex-wrap gap-2">
                    {VEHICLES.map((t) => (
                      <button type="button" key={t} data-testid={`biz-type-${t.split(" ")[0].toLowerCase()}`} onClick={() => toggleType(t)}
                        className={`rounded-full border px-4 py-2 text-sm transition-all ${types.includes(t) ? "border-[#7EC8E3] bg-[#7EC8E3]/15 text-[#A0E0FF]" : "border-slate-500/30 text-slate-300 hover:border-slate-400/60"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <input data-testid="biz-count" type="number" min="1" className={inputCls} placeholder="Numero mezzi *" required value={form.vehicles_count} onChange={(e) => set("vehicles_count", e.target.value)} />
                    {isFleet && <p data-testid="biz-fleet-badge" className="text-xs text-green-300 mt-1.5">Flotta oltre 5 mezzi: prezzi agevolati!</p>}
                  </div>
                  <input data-testid="biz-location" className={inputCls} placeholder="Luogo / sede (città) *" required value={form.location} onChange={(e) => set("location", e.target.value)} />
                </div>
                <textarea data-testid="biz-notes" className={`${inputCls} mt-3 resize-none`} rows={3} placeholder="Note: frequenza desiderata, orari, esigenze particolari (opzionale)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                <button type="submit" data-testid="biz-submit" disabled={sending} className="btn-silver w-full rounded-full py-4 mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {sending ? <><Loader2 size={18} className="animate-spin" /> Invio…</> : "Richiedi preventivo gratuito"}
                </button>
                <p className="text-[11px] text-slate-500 text-center mt-3">Senza impegno. Ti rispondiamo entro 24-48 ore.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
